import { Link } from "@tanstack/react-router";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import {
  ArrowRight,
  BarChart3,
  Building,
  MessageCircle,
  Network,
  Package,
  Users,
  Wallet,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fadeUp, stagger } from "@/lib/animations";

interface CapabilityProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const Capability = ({ icon: Icon, title, description }: CapabilityProps) => (
  <div className="flex gap-4">
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-neutral-700" aria-hidden="true" />
    <div>
      <h3 className="font-semibold text-neutral-900">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-neutral-600">{description}</p>
    </div>
  </div>
);

const AlphaPage = () => {
  usePageSeo(pageSeo.alpha);
  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative w-full overflow-hidden py-20">
        <img
          src="/allimages/developer.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover scale-105"
          style={{ filter: "blur(4px)" }}
        />
        <div className="absolute inset-0 bg-white/80" />
        <motion.div
          className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={fadeUp}>
            <Network className="mx-auto mb-6 h-12 w-12 text-neutral-700" aria-hidden="true" />
            <Badge variant="outline" className="mb-4 text-xs font-medium text-neutral-500">
              Business management platform
            </Badge>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="mb-4 text-4xl font-bold tracking-tight text-neutral-900 md:text-5xl"
          >
            Alpha
          </motion.h1>
          <motion.p variants={fadeUp} className="mb-8 text-xl font-medium text-neutral-500">
            Operations visibility for distributed businesses
          </motion.p>
          <motion.p variants={fadeUp} className="mx-auto max-w-2xl text-lg leading-relaxed text-neutral-600">
            Alpha gives organisations real-time control over their operations — from
            stock and customer records to agent networks and banking partners — through
            a single, unified platform with built-in WhatsApp integration.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Problem / Solution ── */}
      <section className="w-full py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid gap-12 lg:grid-cols-2"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-neutral-900">The problem it solves</h2>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Businesses with distributed agent networks often struggle with fragmented
                information: stock data in one system, customer records in another, agent
                activity tracked on spreadsheets. The result is poor visibility, slow
                decisions, and customer service that doesn't reflect what's actually in stock.
              </p>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Alpha brings all of that into one system, so managers have a live view of the
                business and agents can serve customers accurately — without calling the office
                to check availability.
              </p>
            </motion.div>
            <motion.div variants={fadeUp}>
              <h2 className="text-2xl font-bold text-neutral-900">How Alpha works</h2>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Organisations configure their stock, customer base, and agent structure in
                Alpha. Agents interact with the system — and with customers — via WhatsApp,
                using a familiar interface that requires no app installation on the customer's
                side. Managers and bank partners get dashboards with the data they need.
              </p>
              <p className="mt-4 leading-relaxed text-neutral-600">
                Because Alpha is built around WhatsApp as the communication layer, it meets
                customers where they already are — making the platform practical in markets
                where mobile penetration is high but app adoption is low.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Capabilities ── */}
      <section className="w-full bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-12 text-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-neutral-900">What Alpha includes</h2>
            <p className="mt-4 text-neutral-600">
              Core capabilities designed around how distributed organisations actually operate.
            </p>
          </motion.div>
          <motion.div
            className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {[
              { icon: Package, title: "Stock management", description: "Real-time inventory tracking across locations. Agents see accurate stock levels and can process transactions without leaving the WhatsApp interface." },
              { icon: Users, title: "Customer records", description: "Centralised customer profiles linked to transaction history, making it easy to track relationships and serve returning customers consistently." },
              { icon: Network, title: "Agent network management", description: "Onboard, monitor, and manage distributed agent networks from a single dashboard. Track agent activity and performance in real time." },
              { icon: Building, title: "Bank partner integration", description: "Bank partners get visibility into relevant transaction data, reducing reconciliation friction and enabling tighter financial oversight." },
              { icon: MessageCircle, title: "WhatsApp-native communication", description: "Customer interactions happen over WhatsApp — no separate app needed. Agents process enquiries, transactions, and follow-ups in a familiar interface." },
              { icon: BarChart3, title: "Reporting and dashboards", description: "Management dashboards surface key metrics: stock levels, agent activity, customer acquisition, and transaction volumes — all updated in real time." },
              { icon: Wallet, title: "Payment tracking", description: "Track payments, outstanding balances, and settlements across the agent network, with clear audit trails for reconciliation." },
            ].map(({ icon, title, description }) => (
              <motion.div key={title} variants={fadeUp}>
                <Capability icon={icon} title={title} description={description} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Goldenclem statement ── */}
      <section className="w-full border-t border-neutral-100 bg-stone-50 py-10">
        <motion.div
          className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p className="text-sm text-neutral-500">
            Alpha is deployed by Arsoc for client organisations including{" "}
            <strong className="text-neutral-700">Goldenclem</strong>.
          </p>
        </motion.div>
      </section>

      {/* ── CTA ── */}
      <section className="w-full bg-neutral-950 py-16">
        <motion.div
          className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 variants={fadeUp} className="text-2xl font-bold text-white">
            Interested in Alpha?
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-3 text-neutral-300">
            Let us know about your business and we'll walk you through how Alpha
            could fit your operations.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-6">
            <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90">
              <Link to="/contact" search={{ subject: "Alpha Enquiry" } as Record<string, string>}>
                Get in touch <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default AlphaPage;
